import { useState, useEffect, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { Plus, ChevronDown, ChevronUp, UserPlus, Edit2, X, Eye, Users } from "lucide-react";
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { API_BASE_URL, ENDPOINTS } from './config';
import { supabase } from './lib/supabase';
import { useAuth } from './contexts/AuthContext';
import ApplicantsList from './components/ApplicantsList';

export default function Dashboard() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [location, setLocation] = useState("");
  const [status, setStatus] = useState("open");
  const [jobPostings, setJobPostings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [user, setUser] = useState(null);
  const token = localStorage.getItem("token");
  
  const TITLE_PLACEHOLDER = "Sr Software Engineer";
  const DESCRIPTION_PLACEHOLDER = "We are looking for a talented software engineer to join our team and help build scalable applications. The ideal candidate should have 3+ years of experience in JavaScript, Python, and cloud technologies. Responsibilities include developing new features, fixing bugs, and collaborating with team members. Must be familiar with agile methodologies. Experience with AWS is preferred but not mandatory. Candidates should have good communication skills and problem-solving ability. We offer a competitive salary and a flexible work environment. Apply now to be a part of our innovative team!";
  const LOCATION_PLACEHOLDER = "Bengaluru (Remote)";

  const jobItemStyle = {
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  };

  const [activeTab, setActiveTab] = useState('create');
  const [selectedJob, setSelectedJob] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingJobId, setEditingJobId] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [isJobDetailsExpanded, setIsJobDetailsExpanded] = useState(true);
  const [applicants, setApplicants] = useState([]);
  const [showApplicants, setShowApplicants] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [matchResults, setMatchResults] = useState(null);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [loadingDummyApplicant, setLoadingDummyApplicant] = useState(false);
  const [dummyApplicantJobId, setDummyApplicantJobId] = useState(null);
  const [showResumeExtract, setShowResumeExtract] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [appPage, setAppPage] = useState(1);
  const [appLimit, setAppLimit] = useState(10);
  const [appTotalPages, setAppTotalPages] = useState(1);
  const [appTotalCount, setAppTotalCount] = useState(0);
  const [appSearch, setAppSearch] = useState('');
  const [appSortBy, setAppSortBy] = useState('created_at');
  const [appSortOrder, setAppSortOrder] = useState('desc');

  const navigate = useNavigate();
  const routerLocation = useLocation();

  const fetchJobPostings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.JOBS}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to fetch");
      
      console.log("Received job postings from server:", data);
      setJobPostings(data);
    } catch (err) {
      console.error("Error:", err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchJobPostings();
  }, [fetchJobPostings]);

  useEffect(() => {
    console.log("jobPostings state updated:", jobPostings);
  }, [jobPostings]);

  useEffect(() => {
    if (routerLocation.state?.showJobId) {
      const job = jobPostings.find(j => j.job_id === routerLocation.state.showJobId);
      if (job) {
        setSelectedJob(job);
        setActiveTab('view');
        
        // Set the expanded state based on the navigation state
        setIsJobDetailsExpanded(routerLocation.state.expandJobDetails !== false);
        
        // If showApplicants is true, fetch and show the applicants
        if (routerLocation.state.showApplicants) {
          fetchApplicants(routerLocation.state.showJobId);
        }
      }

      if (routerLocation.state.applicantAdded) {
        setSuccessMessage({
          title: routerLocation.state.jobTitle,
          applicantName: routerLocation.state.applicantName,
          isApplicantAdded: true,
          timestamp: new Date()
        });
      }

      // Only clear the state if it's not a replace navigation
      if (!routerLocation.state.preserve) {
        window.history.replaceState({}, document.title);
      }
    }

    // Restore search state if returning from candidate details
    if (routerLocation.state?.searchQuery && routerLocation.state?.searchResults) {
      setSearchQuery(routerLocation.state.searchQuery);
      setSearchResults(routerLocation.state.searchResults);
    }
  }, [routerLocation, jobPostings]);

  // Add useEffect to fetch user information
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/users/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (response.ok) {
          setUser(data.user);
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    };

    fetchUserInfo();
  }, [token]);

  console.log("Job postings LOGS started");

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CREATE_JOB}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          description,
          location,
          status: "open",
          notes
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Failed to create job posting");
      }

      // Set success message with the job title
      setSuccessMessage({
        title: title,
        timestamp: new Date()
      });

      // Clear the form
      setTitle("");
      setDescription("");
      setNotes("");
      setLocation("");
      setStatus("open");
      
      // Refresh the job postings list
      fetchJobPostings();

      // Auto-dismiss success message after 10 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 10000);

    } catch (error) {
      console.error("Error:", error.message);
      alert("Failed to create job posting: " + error.message);
    }
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.JOB_DETAILS(editingJobId)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          description,
          location,
          status: "open",
          notes
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Failed to update job posting");
      }

      // Clear any existing error
      setErrorMessage(null);

      // Set success message
      setSuccessMessage({
        title: title,
        timestamp: new Date(),
        isUpdate: true
      });

      // Clear the form and edit mode
      setTitle("");
      setDescription("");
      setNotes("");
      setLocation("");
      setStatus("open");
      setIsEditing(false);
      setEditingJobId(null);
      
      // Refresh the job postings list
      fetchJobPostings();

      // Auto-dismiss success message after 10 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 10000);

    } catch (error) {
      console.error("Error:", error.message);
      setErrorMessage(error.message);
      // Auto-dismiss error message after 10 seconds
      setTimeout(() => {
        setErrorMessage(null);
      }, 10000);
    }
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setActiveTab('view');
    setShowApplicants(false);
    setIsJobDetailsExpanded(true);
    setApplicants([]);
  };

  const handleCloseView = () => {
    setSelectedJob(null);
    setActiveTab('create');
  };

  const handleAddNew = () => {
    if (isEditing) {
      setShowWarning(true);
      return;
    }
    
    setTitle("");
    setDescription("");
    setNotes("");
    setLocation("");
    setStatus("open");
    setIsEditing(false);
    setEditingJobId(null);
    setSelectedJob(null);
    setActiveTab('create');
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const handleProceedWithNew = () => {
    setShowWarning(false);
    setIsEditing(false);
    setEditingJobId(null);
    setTitle("");
    setDescription("");
    setNotes("");
    setLocation("");
    setStatus("open");
    setSelectedJob(null);
    setActiveTab('create');
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const fetchApplicants = async (jobId, { page = appPage, limit = appLimit, search = appSearch, sort_by = appSortBy, sort_order = appSortOrder } = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (search) params.append('search', search);
      params.append('sort_by', sort_by);
      params.append('sort_order', sort_order);
      const url = `${API_BASE_URL}${ENDPOINTS.APPLICANTS_FOR_JOB(jobId)}?${params.toString()}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to fetch applicants");
      setApplicants(result.data.applications);
      setAppTotalCount(result.data.pagination.total);
      setAppPage(result.data.pagination.page);
      setAppLimit(result.data.pagination.limit);
      setAppTotalPages(result.data.pagination.total_pages);
      setShowApplicants(true);
      setIsJobDetailsExpanded(false);
    } catch (error) {
      console.error("Error:", error.message);
      alert("Failed to fetch applicants: " + error.message);
    }
  };

  const evaluateMatch = async (jobseekerId, jobId) => {
    setLoadingMatch(true);
    try {
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.EVALUATE_MATCH}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          jobseeker_id: jobseekerId,
          job_id: jobId
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to evaluate match');
      }

      setMatchResults({
        applicantId: jobseekerId,
        ...data.data.evaluation
      });
    } catch (error) {
      console.error('Error evaluating match:', error);
      alert('Failed to evaluate match: ' + error.message);
    } finally {
      setLoadingMatch(false);
    }
  };

  const handleGenerateDummy = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.DUMMY_APPLICANT(selectedJob.job_id)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to add dummy applicant');
      }

      // Show success message
      setSuccessMessage({
        title: selectedJob.title,
        applicantName: `${data.data.generated_data.first_name} ${data.data.generated_data.last_name}`,
        isApplicantAdded: true,
        timestamp: new Date()
      });

      // Refresh the applicants list
      fetchApplicants(selectedJob.job_id);
    } catch (error) {
      console.error('Error adding dummy applicant:', error);
      alert('Failed to add dummy applicant: ' + error.message);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setSearchError(null);
    try {
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.SEARCH_JOBSEEKERS}?query=${encodeURIComponent(searchQuery)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to search jobseekers');
      
      setSearchResults(data);
    } catch (error) {
      console.error('Error searching jobseekers:', error);
      setSearchError(error.message);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
  };

  const handleDeleteJob = async (jobId) => {
    try {
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.JOB_DETAILS(jobId)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete job posting');
      }

      // Show success message
      setSuccessMessage({
        title: selectedJob.title,
        isDelete: true,
        timestamp: new Date()
      });

      // Clear the selected job and refresh the list
      setSelectedJob(null);
      setActiveTab('create');
      fetchJobPostings();
    } catch (error) {
      console.error('Error deleting job posting:', error);
      setErrorMessage(error.message);
      setTimeout(() => setErrorMessage(null), 10000);
    } finally {
      setShowDeleteConfirm(false);
      setJobToDelete(null);
    }
  };

  const handleApplicantsSearch = () => {
    setAppPage(1);
    fetchApplicants(selectedJob.job_id, { page: 1, limit: appLimit, search: appSearch, sort_by: appSortBy, sort_order: appSortOrder });
  };

  const handleApplicantsClearSearch = () => {
    setAppSearch('');
    setAppPage(1);
    fetchApplicants(selectedJob.job_id, { page: 1, limit: appLimit, search: '', sort_by: appSortBy, sort_order: appSortOrder });
  };

  const fetchApplicantsWithParams = (page = appPage) => {
    setAppPage(page);
    fetchApplicants(selectedJob.job_id, { page, limit: appLimit, search: appSearch, sort_by: appSortBy, sort_order: appSortOrder });
  };

  return (
    <div className="d-flex vh-100">
      {/* Sidebar */}
      <aside className="bg-light p-4 border-end" style={{ width: "250px" }}>
        <div className="d-flex flex-column h-100">
          <h2 className="h5">Hiyaa - Dashboard</h2>
          <nav className="mt-4 d-flex flex-column flex-grow-1">
            <div>
              <h3 className="h6 text-secondary">Job Postings</h3>
              
              {/* Add job titles list */}
              <div className="mb-3">
                {loading ? (
                  <p className="text-muted small">Loading jobs...</p>
                ) : jobPostings.length > 0 ? (
                  <ul className="list-unstyled">
                    {jobPostings.map((job) => (
                      <li 
                        key={job.job_id} 
                        className={`py-2 px-2 border-bottom text-truncate ${selectedJob?.job_id === job.job_id ? 'bg-primary text-white' : ''}`}
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          borderRadius: '4px',
                          marginBottom: '2px'
                        }}
                        title={job.title}
                        onClick={() => handleJobClick(job)}
                        onMouseEnter={(e) => {
                          if (selectedJob?.job_id !== job.job_id) {
                            e.target.style.backgroundColor = '#f0f0f0';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedJob?.job_id !== job.job_id) {
                            e.target.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {job.title}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted small">No job postings yet</p>
                )}
              </div>
            </div>

            <button 
              className="btn btn-outline-primary w-100 mt-2 d-flex align-items-center gap-2"
              onClick={handleAddNew}
            >
              <Plus size={16} /> Add new
            </button>

            {/* Search Jobseekers Section */}
            <div className="mt-4">
              <h3 className="h6 text-secondary">Search Jobseekers</h3>
              <div className="input-group mb-2">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                />
                {searchQuery && (
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleClearSearch}
                    title="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
                <button 
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleSearch}
                  disabled={searching || !searchQuery.trim()}
                >
                  {searching ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    'Search'
                  )}
                </button>
              </div>

              {searchError && (
                <div className="alert alert-danger py-2 px-3 small mb-2">
                  {searchError}
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="search-results">
                  <small className="text-muted d-block mb-2">
                    Found {searchResults.length} matching candidates
                  </small>
                  <ul className="list-unstyled">
                    {searchResults.map((jobseeker) => (
                      <li 
                        key={jobseeker.jobseeker_id}
                        className="py-2 px-2 border-bottom text-truncate"
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          borderRadius: '4px',
                          marginBottom: '2px'
                        }}
                        onClick={() => navigate(`/candidate/${jobseeker.jobseeker_id}`, {
                          state: { 
                            from: 'search',
                            searchQuery,
                            searchResults,
                            jobId: selectedJob?.job_id
                          }
                        })}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        {jobseeker.first_name} {jobseeker.last_name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Logout Button */}
            <div className="mt-auto pt-4 border-top">
              <button 
                className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2"
                onClick={async () => {
                  try {
                    await supabase.auth.signOut();
                    // Clear local storage
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    
                    // Navigate to login page
                    navigate("/login");
                  } catch (error) {
                    console.error('Logout error:', error);
                    // Even if logout fails, clear local storage and redirect
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/login");
                  }
                }}
              >
                <X size={16} /> Logout
              </button>
            </div>
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-grow-1 d-flex flex-column">
        <h1 className="h3 p-4 mb-0">Welcome {user?.first_name || 'User'}!</h1>

        {/* Error Message Alert */}
        {errorMessage && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <h4 className="alert-heading">⚠️ Update Failed</h4>
            <p>{errorMessage}</p>
            <hr />
            <p className="mb-0">
              Please make sure you have permission to edit this job posting. If the problem persists, contact support.
            </p>
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setErrorMessage(null)} 
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Success Message Alert */}
        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <h4 className="alert-heading">
              {successMessage.isApplicantAdded 
                ? '🎉 New Applicant Added!'
                : successMessage.isUpdate 
                  ? '✅ Job Posting Updated!' 
                  : successMessage.isDelete
                    ? '🗑️ Job Posting Deleted!'
                    : '🎉 Job Posting Created!'}
            </h4>
            <p>
              {successMessage.isApplicantAdded ? (
                <>
                  <strong>{successMessage.applicantName}</strong> has been successfully added as an applicant for <strong>"{successMessage.title}"</strong>
                </>
              ) : successMessage.isDelete ? (
                <>
                  <strong>"{successMessage.title}"</strong> has been successfully deleted.
                </>
              ) : (
                <>
                  <strong>"{successMessage.title}"</strong> has been successfully 
                  {successMessage.isUpdate ? ' updated.' : ' created and added to your job postings.'}
                </>
              )}
            </p>
            <hr />
            {successMessage.isApplicantAdded ? (
              <p className="mb-0">
                You can now track this application and update its status as needed.
              </p>
            ) : successMessage.isDelete ? (
              <p className="mb-0">
                The job posting and all associated data have been removed from the system.
              </p>
            ) : (
              <div className="mb-0">
                <ul className="mt-2">
                  <li>
                    {successMessage.isUpdate 
                      ? 'Make additional updates to this job posting or create a new one using the form below'
                      : 'Create another job posting using the form below'}
                  </li>
                  <li>View {successMessage.isUpdate ? 'the updated' : 'your newly created'} posting in the list on the left sidebar</li>
                </ul>
              </div>
            )}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setSuccessMessage(null)} 
              aria-label="Close"
            ></button>
          </div>
        )}

        {selectedJob ? (
          <div className="flex-grow-1 d-flex flex-column">
            {/* Collapsible Job Details */}
            <div className="px-4 py-3 border-bottom">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-4">
                  <h2 className="h5 mb-0">{selectedJob.title}</h2>
                  <span className="text-muted">|</span>
                  <span>{selectedJob.location}</span>
                  <span className="text-muted">|</span>
                  <span className={`badge ${selectedJob.status === 'open' ? 'bg-success' : 'bg-secondary'}`}>
                    {selectedJob.status.charAt(0).toUpperCase() + selectedJob.status.slice(1)}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button 
                    className="btn btn-outline-primary btn-sm"
                    title="View Applicants"
                    onClick={() => fetchApplicants(selectedJob.job_id)}
                  >
                    <Users size={16} /> View Applicants
                  </button>
                  <button 
                    className="btn btn-primary btn-sm"
                    title="Add Applicant"
                    onClick={() => navigate(`/add-applicant/${selectedJob.job_id}`)}
                  >
                    <UserPlus size={16} /> Add Applicant
                  </button>
                  <button 
                    className="btn btn-outline-primary btn-sm"
                    title="Add Dummy Applicant"
                    onClick={handleGenerateDummy}
                    disabled={loadingDummyApplicant}
                  >
                    <UserPlus size={16} /> {loadingDummyApplicant ? 'Adding...' : 'Dummy'}
                  </button>

                  {/* Dummy Applicant Loading Modal */}
                  {loadingDummyApplicant && dummyApplicantJobId === selectedJob.job_id && (
                    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                      <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                          <div className="modal-body text-center py-5">
                            <div className="spinner-border text-primary mb-3" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <h5 className="mb-3">Generating Dummy Applicant</h5>
                            <p className="text-muted mb-0">
                              Our AI is creating a realistic applicant profile that matches the job requirements...
                            </p>
                            <div className="mt-4">
                              <div className="d-flex justify-content-center gap-3">
                                <small className="text-muted">
                                  <span className="spinner-grow spinner-grow-sm me-1"></span>
                                  Generating profile
                                </small>
                                <small className="text-muted">
                                  <span className="spinner-grow spinner-grow-sm me-1"></span>
                                  Creating account
                                </small>
                                <small className="text-muted">
                                  <span className="spinner-grow spinner-grow-sm me-1"></span>
                                  Adding application
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <button 
                    className="btn btn-outline-primary btn-sm"
                    title="Edit Job"
                    onClick={() => {
                      setTitle(selectedJob.title);
                      setDescription(selectedJob.description);
                      setLocation(selectedJob.location);
                      setNotes(selectedJob.notes || '');
                      setIsEditing(true);
                      setEditingJobId(selectedJob.job_id);
                      setSelectedJob(null);
                      setActiveTab('create');
                    }}
                  >
                    <Edit2 size={16} /> Edit Posting
                  </button>
                  <button 
                    className="btn btn-outline-danger btn-sm"
                    title="Delete Job"
                    onClick={() => {
                      setJobToDelete(selectedJob);
                      setShowDeleteConfirm(true);
                    }}
                  >
                    <X size={16} /> Delete Posting
                  </button>
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    title="Close"
                    onClick={handleCloseView}
                  >
                    <X size={16} /> Close
                  </button>
                  <button 
                    className="btn btn-outline-secondary btn-sm ms-2"
                    title={isJobDetailsExpanded ? "Collapse" : "Expand"}
                    onClick={() => setIsJobDetailsExpanded(!isJobDetailsExpanded)}
                  >
                    {isJobDetailsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>
              </div>
              
              {/* Expandable Job Details */}
              {isJobDetailsExpanded && (
                <div className="mt-4">
                  <div className="mb-4">
                    <label className="form-label fw-bold">Job Description:</label>
                    <div className="p-3 bg-light rounded">
                      <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                        {selectedJob.description}
                      </pre>
                    </div>
                  </div>

                  {selectedJob.notes && (
                    <div className="mb-4">
                      <label className="form-label fw-bold">Notes:</label>
                      <div className="p-3 bg-light rounded">
                        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                          {selectedJob.notes}
                        </pre>
                      </div>
                    </div>
                  )}

                  <div className="border-top pt-3 mt-4">
                    <small className="text-muted">
                      Created: {new Date(selectedJob.created_at).toLocaleDateString()}
                      <br />
                      Last Updated: {new Date(selectedJob.updated_at).toLocaleDateString()}
                    </small>
                  </div>
                </div>
              )}
            </div>

            {/* Applicants Table */}
            {showApplicants && (
              <ApplicantsList
                applicants={applicants}
                selectedJob={selectedJob}
                appSearch={appSearch}
                setAppSearch={setAppSearch}
                appSortBy={appSortBy}
                setAppSortBy={setAppSortBy}
                appSortOrder={appSortOrder}
                setAppSortOrder={setAppSortOrder}
                appTotalCount={appTotalCount}
                appPage={appPage}
                appTotalPages={appTotalPages}
                handleApplicantsSearch={handleApplicantsSearch}
                handleApplicantsClearSearch={handleApplicantsClearSearch}
                fetchApplicantsWithParams={fetchApplicantsWithParams}
                evaluateMatch={evaluateMatch}
              />
            )}
          </div>
        ) : (
          <div className="p-4">
            <h2 className="h5 mb-4">Create or Upload a Job Description</h2>
            
            <div className="nav-container px-2">
              <ul className="nav nav-tabs" id="jobTabs" role="tablist">
                <li className="nav-item" role="presentation">
                  <button 
                    className={`nav-link ${activeTab === 'create' ? 'active' : ''}`}
                    onClick={() => setActiveTab('create')}
                  >
                    Create
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button 
                    className={`nav-link ${activeTab === 'upload' ? 'active' : ''}`}
                    onClick={() => setActiveTab('upload')}
                  >
                    Upload
                  </button>
                </li>
              </ul>
            </div>

            <div className="tab-content mt-4 px-2">
              <div className={`tab-pane fade ${activeTab === 'create' ? 'show active' : ''}`}>
                <div className="card">
                  <div className="card-body p-4">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Title:</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={TITLE_PLACEHOLDER}
                        style={{
                          color: title ? 'black' : '#6c757d'
                        }}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-bold">Location:</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder={LOCATION_PLACEHOLDER}
                        style={{
                          color: location ? 'black' : '#6c757d'
                        }}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-bold">Status:</label>
                      <select 
                        className="form-select" 
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        disabled
                      >
                        <option value="open">Open</option>
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-bold">Job Description:</label>
                      <textarea 
                        className="form-control" 
                        rows="6" 
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={DESCRIPTION_PLACEHOLDER}
                        style={{
                          color: description ? 'black' : '#6c757d'
                        }}
                      ></textarea>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-bold">Notes:</label>
                      <textarea
                        className="form-control"
                        placeholder="My Notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      ></textarea>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <div className="rounded-circle bg-secondary" style={{ width: "40px", height: "40px" }}></div>
                      <button 
                        className={`btn ${isEditing ? 'btn-success' : 'btn-primary'}`}
                        onClick={isEditing ? handleUpdate : handleSubmit}
                        disabled={!title.trim() || !description.trim() || !location.trim()}
                      >
                        {isEditing ? 'Update' : 'Submit'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`tab-pane fade ${activeTab === 'upload' ? 'show active' : ''}`}>
                <div className="card p-5 text-center">
                  <div className="mb-4">
                    <i className="fas fa-rocket fs-1 text-primary"></i>
                  </div>
                  <h3 className="h4 mb-3">Coming Soon!</h3>
                  <p className="text-muted mb-0">
                    We're working on making it easier for you to upload job descriptions directly from files.
                    <br />
                    In the meantime, you can create job postings using our form in the Create tab.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {showWarning && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">⚠️ Unsaved Changes</h5>
                <button type="button" className="btn-close" onClick={() => setShowWarning(false)}></button>
              </div>
              <div className="modal-body">
                <p>You are currently editing a job posting. If you proceed, your unsaved changes will be lost.</p>
                <p>Do you want to continue creating a new job posting?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowWarning(false)}>
                  Continue Editing
                </button>
                <button type="button" className="btn btn-primary" onClick={handleProceedWithNew}>
                  Create New
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resume Extract Modal */}
      {showResumeExtract && selectedApplicant && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Resume Extract - {selectedApplicant.candidate.first_name} {selectedApplicant.candidate.last_name}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowResumeExtract(false);
                    setSelectedApplicant(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {selectedApplicant.candidate.resume_extract ? (
                  <div className="resume-extract" style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedApplicant.candidate.resume_extract}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">No resume extract available</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowResumeExtract(false);
                    setSelectedApplicant(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && jobToDelete && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">⚠️ Delete Job Posting</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setJobToDelete(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this job posting?</p>
                <div className="alert alert-warning">
                  <strong>{jobToDelete.title}</strong>
                  <br />
                  <small className="text-muted">
                    Location: {jobToDelete.location}
                    <br />
                    Status: {jobToDelete.status}
                  </small>
                </div>
                <p className="text-danger">
                  <small>
                    This action will also delete all related applications, notes, and other associated data.
                    This action cannot be undone.
                  </small>
                </p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setJobToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={() => handleDeleteJob(jobToDelete.job_id)}
                >
                  Delete Job Posting
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}